import Flow from 'flow';
import { openCallWidgetLink } from 'flow/actions/run-url-actions';
import CallWidget from 'flow/components/call-components/call-widget/CallWidget';
import Divider from 'flow/components/ui-components/Divider';
import Icon from 'flow/components/ui-components/Icon';
import MenuItem from 'flow/components/ui-components/MenuItem';
import NestedMenuItem from 'flow/components/ui-components/NestedMenuItem';
import useContact from 'flow/hooks/useContact';
import {
    getVersion1CallWidgets,
    getVersion2CallWidgets,
} from 'flow/selectors/call-widget-selectors';
import { getOwnCountryCode } from 'flow/selectors/selectors';
import {
    buildWidgetDataFromConfig,
    chooseConfig,
    getHistoricCallVariables,
} from 'flow/utils/call-widget-utils';
import { getContactNumber } from 'flow/utils/contact-utils';
import {
    addContactToBagVariables,
    getSortedExternalSearchCallWidgetUrlsBasedOnNumber,
} from 'flow/utils/run-url-utils';
import PropTypes from 'prop-types';
import React from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { connect } from 'react-redux';

export const CallWidgetMenuItems = (props) => {
    const {
        externalSearchUrls,
        phoneNumber,
        handleClose,
        version2CallWidgets,
        ownCountryCode,
        isCallHistoryCard,
        hasCallWidgets,
        callRecord,
    } = props;

    const { ownContact } = useContact();
    const ownNumber = getContactNumber(ownContact);

    if (
        !phoneNumber ||
        (externalSearchUrls.length === 0 && version2CallWidgets.length === 0)
    ) {
        return null;
    }

    const onLinkClick = (event, urlData) => {
        openCallWidgetLink(urlData.url);
        handleClose(event);
    };

    const menuItem = (urlData) => {
        return (
            <MenuItem
                key={urlData.key}
                onClick={(event) => onLinkClick(event, urlData)}
            >
                <div
                    className={
                        isCallHistoryCard ? 'w-9/12 ps-2 text-sm' : 'w-full'
                    }
                >
                    {urlData.title}
                </div>
                <div className="ms-1">
                    <Icon iconKey={'launch'} className="rtl:scale-x-[-1]" />
                </div>
            </MenuItem>
        );
    };

    const version2CallWidget = (widget) => {
        const config = widget.get('config').toJS();
        const variables = getHistoricCallVariables(
            callRecord,
            ownCountryCode,
            ownNumber,
        );
        const chosenConfig = chooseConfig(
            config.discriminator,
            variables,
            config.present,
            config.missing,
        );
        if (!chosenConfig) {
            return null;
        }
        const configWithData = buildWidgetDataFromConfig(
            chosenConfig,
            variables,
        );
        const primaryIcon = configWithData.primaryIcon;
        const primaryLabel = configWithData.header.primaryLabel;
        const hasIconUrl =
            typeof primaryIcon === 'string' &&
            primaryIcon.startsWith('https://');

        const title = (
            <div className="flex gap-2">
                {hasIconUrl ? (
                    // Decorative: primaryLabel is rendered as text beside it.
                    <img
                        className="max-h-6 max-w-6 pe-1"
                        src={primaryIcon}
                        alt=""
                    />
                ) : (
                    primaryIcon
                )}
                <div>{primaryLabel}</div>
            </div>
        );

        return (
            <NestedMenuItem
                key={widget.get('key')}
                title={title}
                allowedAutoPlacements={['bottom', 'top', 'left', 'right']}
                placementDesired="auto"
            >
                <CallWidget
                    present={config.present}
                    missing={config.missing}
                    discriminator={config.discriminator}
                    variables={variables}
                    darkMode={false}
                    width={300}
                />
            </NestedMenuItem>
        );
    };

    return (
        <>
            {hasCallWidgets ? <Divider className="my-2" /> : null}
            {externalSearchUrls &&
                externalSearchUrls.map((urlData) => menuItem(urlData))}
            {version2CallWidgets &&
                version2CallWidgets.map((widget) => {
                    return version2CallWidget(widget);
                })}
        </>
    );
};

CallWidgetMenuItems.propTypes = {
    externalSearchUrls: PropTypes.arrayOf(
        PropTypes.shape({
            url: PropTypes.string,
            name: PropTypes.string,
        }),
    ),
    phoneNumber: PropTypes.string,
    handleClose: PropTypes.func.isRequired,
    version2CallWidgets: ImmutablePropTypes.listOf(
        Flow.PropTypes.callWidgetDTO,
    ),
    contact: Flow.PropTypes.contactDTO,
    ownCountryCode: PropTypes.string,
    isCallHistoryCard: PropTypes.bool,
    hasCallWidgets: PropTypes.bool,
    callRecord: Flow.PropTypes.callRecordDTO,
};

const mapStateToProps = (state, ownProps) => {
    const { callRecord } = ownProps;

    const contact = callRecord?.get('contact');
    const phoneNumber = callRecord?.getIn(['number', 'number']);

    const version1CallWidgets = getVersion1CallWidgets(state);
    const version2CallWidgets = getVersion2CallWidgets(state);

    const ownCountryCode = getOwnCountryCode(state);
    const bagVariables = addContactToBagVariables(contact, {});

    const externalSearchUrls =
        getSortedExternalSearchCallWidgetUrlsBasedOnNumber(
            version1CallWidgets,
            bagVariables,
        );

    const hasCallWidgets =
        (externalSearchUrls && externalSearchUrls.length > 0) ||
        (version2CallWidgets && version2CallWidgets.size > 0);

    return {
        externalSearchUrls,
        version2CallWidgets,
        phoneNumber,
        ownCountryCode,
        hasCallWidgets,
    };
};

export default connect(mapStateToProps)(React.memo(CallWidgetMenuItems));
